import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FieldLabel } from '@/components/FieldLabel'
import { TokenInput } from '@/components/TokenInput'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { NumberInput } from '@/components/ui/number-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  MEDIA_GROUPS,
  OTHER_MEDIA_KINDS,
  mediaGroupOf,
  mimeTypesFor,
  type MediaGroup,
} from '@/lib/mediaTypes'
import type { LayoutComponent } from '@/services/sites/layoutsApi'

export const REQUIRED_OPTIONS = ['MANDATORY', 'MANDATORY_GROUP', 'OPTIONAL']

export const ASPECT_RATIOS = [
  'VERTICAL_9X16',
  'NEAR_SQUARE_VERTICAL_4X5',
  'SQUARE_1X1',
  'NEAR_SQUARE_5X4',
  'SCREEN_4X3',
  'PHOTO_3X2',
  'WIDE_RECTANGLE_8X5',
  'WIDESCREEN_16X9',
  'HORIZONTAL_1_91X1',
  'WIDE_2X1',
  'VERY_WIDE_3X1',
  'SUPER_WIDE_6X1',
]

const ANY = '__any__'
const CUSTOM = 'CUSTOM'

/** What the dialog edits — the component plus the choices the API stores differently. */
interface Draft extends LayoutComponent {
  mediaGroup: MediaGroup
  otherKind?: string
  maxFileSizeKb: number | null
  /** An enum value, CUSTOM for a width:height pair, or ANY. */
  aspectChoice: string
}

function toDraft(component: LayoutComponent): Draft {
  const { group, otherKind } = mediaGroupOf(component.mimeTypes)
  const custom = (component.customAspectRatioWidth ?? 0) > 0 && (component.customAspectRatioHeight ?? 0) > 0
  return {
    ...component,
    mediaGroup: group,
    otherKind,
    maxFileSizeKb: component.maxFileSizeBytes ? Math.round(component.maxFileSizeBytes / 1024) : null,
    aspectChoice: custom ? CUSTOM : component.aspectRatio || ANY,
  }
}

function fromDraft(draft: Draft): LayoutComponent {
  const { mediaGroup, otherKind, maxFileSizeKb, aspectChoice, ...component } = draft
  const base = { type: component.type, tag: component.tag.trim(), name: component.name?.trim() || undefined, required: component.required }
  switch (component.type) {
    case 'ASSET': {
      // A narrower list set elsewhere (only JPEG and PNG, say) is kept unless
      // the media type itself was changed here.
      const before = mediaGroupOf(component.mimeTypes)
      const unchanged = component.mimeTypes?.length && before.group === mediaGroup && before.otherKind === otherKind
      return {
        ...base,
        mimeTypes: unchanged ? component.mimeTypes : mimeTypesFor(mediaGroup, otherKind),
        maxFileSizeBytes: (maxFileSizeKb ?? 0) * 1024,
        aspectRatio: aspectChoice === ANY || aspectChoice === CUSTOM ? undefined : aspectChoice,
        customAspectRatioWidth: aspectChoice === CUSTOM ? component.customAspectRatioWidth : undefined,
        customAspectRatioHeight: aspectChoice === CUSTOM ? component.customAspectRatioHeight : undefined,
      }
    }
    case 'TEXT':
      return {
        ...base,
        textType: component.textType,
        minLength: component.minLength ?? 0,
        // Code has no upper bound; the API reads 0 as "none".
        maxLength: component.textType === 'CODE' ? 0 : component.maxLength,
        defaultValue: component.defaultValue ?? '',
      }
    case 'CHOICE':
      return {
        ...base,
        options: component.options ?? [],
        display: component.display,
        defaultValue: component.defaultValue ?? '',
      }
    default:
      return base
  }
}

function validate(draft: Draft, takenTags: string[], t: (key: string) => string): Record<string, string> {
  const errors: Record<string, string> = {}
  const tag = draft.tag.trim()
  if (!tag) errors.tag = t('layouts.componentEditor.tagRequired')
  else if (/\s/.test(tag)) errors.tag = t('layouts.componentEditor.tagNoSpaces')
  else if (takenTags.includes(tag)) errors.tag = t('layouts.componentEditor.tagTaken')

  if (draft.type === 'ASSET') {
    if (!draft.maxFileSizeKb || draft.maxFileSizeKb < 1) errors.maxFileSizeKb = t('layouts.componentEditor.fileSizeRequired')
    if (draft.mediaGroup === 'other' && !draft.otherKind) errors.otherKind = t('layouts.componentEditor.otherKindRequired')
    if (
      draft.aspectChoice === CUSTOM &&
      !((draft.customAspectRatioWidth ?? 0) > 0 && (draft.customAspectRatioHeight ?? 0) > 0)
    ) {
      errors.aspect = t('layouts.componentEditor.customAspectRequired')
    }
  }
  if (draft.type === 'TEXT' && draft.textType !== 'CODE') {
    if (!draft.maxLength || draft.maxLength < 1) errors.maxLength = t('layouts.componentEditor.maxLengthRequired')
    else if ((draft.minLength ?? 0) > draft.maxLength) errors.maxLength = t('layouts.componentEditor.maxBelowMin')
  }
  if (draft.type === 'CHOICE' && !draft.options?.length) {
    errors.options = t('layouts.componentEditor.optionsRequired')
  }
  return errors
}

export interface LayoutComponentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The component to edit — a fresh one with defaults when adding. */
  component: LayoutComponent | null
  /** True when the component is not in the layout yet, which only changes the wording. */
  isNew: boolean
  /** Tags the other components use, which this one may not take. */
  takenTags: string[]
  onSave: (component: LayoutComponent) => void
}

/**
 * Edits one layout component. The common fields come first — tag, label and
 * whether a creative must fill it in — then the fields of its type. Nothing
 * reaches the layout until Apply, so Cancel leaves it as it was.
 */
export function LayoutComponentDialog({
  open,
  onOpenChange,
  component,
  isNew,
  takenTags,
  onSave,
}: LayoutComponentDialogProps) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<Draft | null>(null)
  const [submitted, setSubmitted] = useState(false)

  // Opening is what seeds the editor, so later prop churn keeps the edits.
  useEffect(() => {
    if (!open || !component) return
    setDraft(toDraft(component))
    setSubmitted(false)
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!draft) return null

  const errors = validate(draft, takenTags, t)
  const shown = submitted ? errors : {}
  const set = (changes: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...changes } : d))

  const apply = () => {
    setSubmitted(true)
    if (Object.keys(errors).length) return
    onSave(fromDraft(draft))
    onOpenChange(false)
  }

  const typeLabel = t(`layouts.componentTypes.${draft.type}`)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {t(isNew ? 'layouts.componentEditor.addTitle' : 'layouts.componentEditor.editTitle', { type: typeLabel })}
          </DialogTitle>
          <DialogDescription>{t(`layouts.componentTypeHints.${draft.type}`)}</DialogDescription>
        </DialogHeader>

        <form
          id="layout-component-form"
          className="field-grid"
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            apply()
          }}
        >
          <div className="p-field">
            <FieldLabel htmlFor="lc-tag" tooltip={t('layouts.componentEditor.tagHelp')}>
              {t('layouts.componentEditor.tag')}
            </FieldLabel>
            <Input
              id="lc-tag"
              value={draft.tag}
              onChange={(e) => set({ tag: e.target.value })}
              aria-invalid={!!shown.tag}
              className="font-mono"
              autoFocus
            />
            {shown.tag && <p className="text-xs text-destructive">{shown.tag}</p>}
          </div>

          <div className="p-field">
            <FieldLabel htmlFor="lc-name" tooltip={t('layouts.componentEditor.labelHelp')} hint={t('common.optional')}>
              {t('layouts.componentEditor.label')}
            </FieldLabel>
            <Input
              id="lc-name"
              value={draft.name ?? ''}
              onChange={(e) => set({ name: e.target.value })}
              placeholder={draft.tag}
            />
          </div>

          <div className="p-field">
            <FieldLabel tooltip={t('layouts.componentEditor.requiredHelp')}>
              {t('layouts.componentEditor.required')}
            </FieldLabel>
            <Select
              label={t('layouts.componentEditor.required')}
              value={draft.required}
              onValueChange={(v) => set({ required: v })}
            >
              <SelectTrigger>
                <SelectValue>{t(`layouts.required.${draft.required}`)}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {REQUIRED_OPTIONS.map((o) => (
                  <SelectItem key={o} value={o}>{t(`layouts.required.${o}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {draft.type === 'ASSET' && (
            <>
              <div className="p-field">
                <FieldLabel>{t('layouts.componentEditor.mediaType')}</FieldLabel>
                <Select
                  label={t('layouts.componentEditor.mediaType')}
                  value={draft.mediaGroup}
                  onValueChange={(v) => set({ mediaGroup: v as MediaGroup, otherKind: undefined })}
                >
                  <SelectTrigger>
                    <SelectValue>{t(`layouts.mediaGroups.${draft.mediaGroup}`)}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {MEDIA_GROUPS.map((g) => (
                      <SelectItem key={g} value={g}>{t(`layouts.mediaGroups.${g}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {draft.mediaGroup === 'other' && (
                <div className="p-field">
                  <FieldLabel>{t('layouts.componentEditor.fileKind')}</FieldLabel>
                  <Select
                    label={t('layouts.componentEditor.fileKind')}
                    value={draft.otherKind ?? ''}
                    onValueChange={(v) => set({ otherKind: v })}
                  >
                    <SelectTrigger aria-invalid={!!shown.otherKind}>
                      <SelectValue placeholder={t('layouts.componentEditor.selectFileKind')}>
                        {draft.otherKind ? t(`layouts.mediaKinds.${draft.otherKind}`) : undefined}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {OTHER_MEDIA_KINDS.map((k) => (
                        <SelectItem key={k.id} value={k.id}>{t(`layouts.mediaKinds.${k.id}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {shown.otherKind && <p className="text-xs text-destructive">{shown.otherKind}</p>}
                </div>
              )}

              <div className="p-field">
                <FieldLabel htmlFor="lc-size" hint="KB">{t('layouts.componentEditor.maxFileSize')}</FieldLabel>
                <NumberInput
                  id="lc-size"
                  min={1}
                  value={draft.maxFileSizeKb}
                  onChange={(v) => set({ maxFileSizeKb: v })}
                  aria-invalid={!!shown.maxFileSizeKb}
                />
                {shown.maxFileSizeKb && <p className="text-xs text-destructive">{shown.maxFileSizeKb}</p>}
              </div>

              <div className="p-field">
                <FieldLabel>{t('layouts.componentEditor.aspectRatio')}</FieldLabel>
                <Select
                  label={t('layouts.componentEditor.aspectRatio')}
                  value={draft.aspectChoice}
                  onValueChange={(v) => set({ aspectChoice: v })}
                >
                  <SelectTrigger>
                    <SelectValue>{t(`layouts.aspectRatios.${draft.aspectChoice}`)}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {[ANY, ...ASPECT_RATIOS, CUSTOM].map((a) => (
                      <SelectItem key={a} value={a}>{t(`layouts.aspectRatios.${a}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {draft.aspectChoice === CUSTOM && (
                <div className="p-field">
                  <FieldLabel hint={t('layouts.componentEditor.widthToHeight')}>
                    {t('layouts.componentEditor.customAspectRatio')}
                  </FieldLabel>
                  <div className="flex items-center gap-2">
                    <NumberInput
                      min={0}
                      value={draft.customAspectRatioWidth}
                      onChange={(v) => set({ customAspectRatioWidth: v ?? undefined })}
                      aria-invalid={!!shown.aspect}
                    />
                    <span className="text-(--ad-text-muted)">:</span>
                    <NumberInput
                      min={0}
                      value={draft.customAspectRatioHeight}
                      onChange={(v) => set({ customAspectRatioHeight: v ?? undefined })}
                      aria-invalid={!!shown.aspect}
                    />
                  </div>
                  {shown.aspect && <p className="text-xs text-destructive">{shown.aspect}</p>}
                </div>
              )}
            </>
          )}

          {draft.type === 'TEXT' && (
            <>
              <div className="p-field">
                <FieldLabel tooltip={t('layouts.componentEditor.textTypeHelp')}>
                  {t('layouts.componentEditor.textType')}
                </FieldLabel>
                <Select
                  label={t('layouts.componentEditor.textType')}
                  value={draft.textType ?? 'TEXT'}
                  onValueChange={(v) => set({ textType: v })}
                >
                  <SelectTrigger>
                    <SelectValue>{t(`layouts.textTypes.${draft.textType ?? 'TEXT'}`)}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {['TEXT', 'CODE'].map((o) => (
                      <SelectItem key={o} value={o}>{t(`layouts.textTypes.${o}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="p-field">
                <FieldLabel htmlFor="lc-min">{t('layouts.componentEditor.minLength')}</FieldLabel>
                <NumberInput
                  id="lc-min"
                  min={0}
                  value={draft.minLength ?? 0}
                  onChange={(v) => set({ minLength: v ?? 0 })}
                />
              </div>

              {draft.textType !== 'CODE' && (
                <div className="p-field">
                  <FieldLabel htmlFor="lc-max">{t('layouts.componentEditor.maxLength')}</FieldLabel>
                  <NumberInput
                    id="lc-max"
                    min={1}
                    value={draft.maxLength}
                    onChange={(v) => set({ maxLength: v ?? undefined })}
                    aria-invalid={!!shown.maxLength}
                  />
                  {shown.maxLength && <p className="text-xs text-destructive">{shown.maxLength}</p>}
                </div>
              )}

              <div className="p-field full">
                <FieldLabel htmlFor="lc-default" tooltip={t('layouts.componentEditor.defaultTextHelp')} hint={t('common.optional')}>
                  {t('layouts.componentEditor.defaultText')}
                </FieldLabel>
                <Input
                  id="lc-default"
                  value={draft.defaultValue ?? ''}
                  onChange={(e) => set({ defaultValue: e.target.value })}
                />
              </div>
            </>
          )}

          {draft.type === 'CHOICE' && (
            <>
              <div className="p-field">
                <FieldLabel>{t('layouts.componentEditor.display')}</FieldLabel>
                <Select
                  label={t('layouts.componentEditor.display')}
                  value={draft.display ?? 'COMBO'}
                  onValueChange={(v) => set({ display: v })}
                >
                  <SelectTrigger>
                    <SelectValue>{t(`layouts.choiceDisplays.${draft.display ?? 'COMBO'}`)}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {['COMBO', 'RADIO'].map((o) => (
                      <SelectItem key={o} value={o}>{t(`layouts.choiceDisplays.${o}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="p-field full">
                <FieldLabel tooltip={t('layouts.componentEditor.optionsHelp')}>
                  {t('layouts.componentEditor.options')}
                </FieldLabel>
                <TokenInput
                  value={draft.options ?? []}
                  onChange={(options) =>
                    set({
                      options,
                      defaultValue: options.includes(draft.defaultValue ?? '') ? draft.defaultValue : '',
                    })
                  }
                  placeholder={t('layouts.componentEditor.optionsPlaceholder')}
                />
                {shown.options && <p className="text-xs text-destructive">{shown.options}</p>}
              </div>

              <div className="p-field">
                <FieldLabel hint={t('common.optional')}>{t('layouts.componentEditor.defaultOption')}</FieldLabel>
                <Select
                  label={t('layouts.componentEditor.defaultOption')}
                  value={draft.defaultValue || ANY}
                  onValueChange={(v) => set({ defaultValue: v === ANY ? '' : v })}
                >
                  <SelectTrigger>
                    <SelectValue>{draft.defaultValue || '—'}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY}>—</SelectItem>
                    {(draft.options ?? []).map((o) => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </form>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form="layout-component-form">
            {t(isNew ? 'layouts.componentEditor.add' : 'common.apply')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
